// Localização: frontend/src/components/Common/PrimaryButton.jsx (Versão 4.0.0 - Estilo Similar ao Botão Período)

import React from 'react';

/**
 * Componente PrimaryButton - Botão moderno com estilo similar ao botão "Período"
 * 
 * @param {React.ReactNode} children - Conteúdo do botão
 * @param {Function} onClick - Função chamada ao clicar
 * @param {string} type - Tipo do botão ('button', 'submit', 'reset')
 * @param {boolean} disabled - Se o botão está desabilitado
 * @param {string} className - Classes CSS adicionais
 * @param {boolean} isAccent - Se deve usar cor de destaque (opcional)
 * @param {React.ReactNode} icon - Ícone a ser exibido antes do texto
 * @param {string} variant - Variante do botão ('primary', 'secondary', 'danger', 'success', 'outline')
 * @param {string} size - Tamanho do botão ('sm', 'md', 'lg')
 * @param {boolean} active - Se o botão está ativo/selecionado (para estilo similar ao Período)
 */
const PrimaryButton = ({ 
    children, 
    onClick, 
    type = 'button', 
    disabled = false, 
    className = '', 
    isAccent = false,
    icon,
    variant = 'primary',
    size = 'md',
    active = false,
    ...restProps
}) => {
    // Variantes de cor - estilo similar ao botão Período
    const variantStyles = {
        primary: active 
            ? 'bg-emerald-100 text-emerald-700 border-emerald-300 border-[0.5px] shadow-md font-semibold'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-400 shadow-sm hover:shadow-md',
        secondary: active
            ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-md font-semibold'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-400 shadow-sm hover:shadow-md',
        danger: active
            ? 'bg-red-100 text-red-700 border-red-300 border-[0.5px] shadow-md font-semibold'
            : 'bg-white text-red-700 border-red-300 hover:bg-gradient-to-br hover:from-red-50 hover:to-red-100 hover:border-red-400 shadow-sm hover:shadow-md',
        success: active
            ? 'bg-green-100 text-green-700 border-green-300 border-[0.5px] shadow-md font-semibold'
            : 'bg-white text-green-700 border-green-300 hover:bg-gradient-to-br hover:from-green-50 hover:to-green-100 hover:border-green-400 shadow-sm hover:shadow-md',
        outline: active
            ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-md font-semibold'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-400 shadow-sm hover:shadow-md',
    };

    // Tamanhos - similar ao botão Período
    const sizeStyles = {
        sm: 'px-3 py-1.5 text-xs sm:text-sm',
        md: 'px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm',
        lg: 'px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base',
    };

    // Estilo base - similar ao botão Período
    const baseStyles = `
        flex items-center justify-center gap-2
        rounded-lg
        transition-all duration-200
        font-medium
        border
        outline-none
        focus:outline-none
        focus-visible:outline-none
        focus-visible:ring-0
        focus:ring-0
        select-none
        user-select-none
        -webkit-tap-highlight-color-transparent
        disabled:opacity-60 disabled:shadow-none disabled:cursor-not-allowed
        disabled:hover:bg-current
        whitespace-nowrap
    `;

    // Combina estilos
    const buttonStyles = `
        ${baseStyles}
        ${variantStyles[variant] || variantStyles.primary}
        ${sizeStyles[size] || sizeStyles.md}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    // Handler para prevenir desfoque
    const handleClick = (e) => {
        if (onClick && !disabled) {
            onClick(e);
        }
        // Remove o foco após o clique para evitar desfoque visual
        if (e.currentTarget) {
            e.currentTarget.blur();
        }
    };

    return (
        <button
            type={type}
            onClick={handleClick}
            disabled={disabled}
            className={buttonStyles}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            {...restProps}
        >
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children}
        </button>
    );
};

export default PrimaryButton;

